<template>
  <paper-modal-container :is-visible="isVisible" :title="modalTitle" :hide-close-button="false" @close="cancelPopup">
    <div :class="$style.input">
      <h4>카테고리 <span style="font-size: 12px">(필수)</span></h4>
      <div :class="$style.selectWrapper">
        <select :class="$style.selectInput" v-model="category" :disabled="uploading" aria-label="카테고리 선택">
          <option disabled value="">카테고리를 선택하세요</option>
          <option v-for="opt in categoryOptions" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
        <span :class="$style.selectArrow" aria-hidden="true">▾</span>
      </div>
      <p :class="$style.selectHint" v-if="!category">원하시는 카테고리를 선택해주세요</p>
    </div>
    <div :class="$style.input">
      <h4>커미션명 <span style="font-size: 12px">(필수)</span></h4>
      <input :class="$style.textInput" v-model="name" placeholder="커미션명 입력" :disabled="uploading" />
    </div>
    <div :class="$style.input">
      <h4>썸네일 <span style="font-size: 12px">(필수)</span></h4>
      <!-- 기존 이미지 미리보기 -->
      <div v-if="isUpdateMode && thumbnailPreviewUrl" :class="$style.imagePreviewContainer">
        <div :class="$style.imagePreview">
          <img :src="thumbnailPreviewUrl" alt="썸네일" :class="$style.previewImage" />
        </div>
      </div>
      <input ref="thumbnailFileInput" :class="$style.fileInput" type="file" @change="handleThumbnailUpload"
        :disabled="uploading" accept="image/*" />
      <div v-if="thumbnailFile" :class="$style.newFileInfo">새 파일: {{ thumbnailFile.name }}</div>
    </div>
    <div :class="$style.input">
      <h4>연결링크 <span style="font-size: 12px">(필수, URL)</span></h4>
      <input :class="$style.textInput" v-model="link" placeholder="https://example.com" :disabled="uploading" />
    </div>
    <div :class="$style.input">
      <h4>설명 <span style="font-size: 12px">(선택)</span></h4>
      <textarea :class="$style.textareaInput" v-model="description" rows="4" placeholder="커미션에 대한 설명을 입력하세요"
        :disabled="uploading"></textarea>
    </div>
    <div :class="$style.buttons">
      <button :class="$style.cancel" @click="cancelPopup" :disabled="uploading">
        취소
      </button>
      <button :class="$style.register" @click="handleSubmit" :disabled="uploading">
        <span v-if="!uploading">{{ submitButtonText }}</span>
        <span v-else> <i :class="$style.spinner"></i> {{ loadingText }}</span>
      </button>
    </div>
  </paper-modal-container>
</template>

<script>
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "/firebaseConfig";
import { createCommission, updateCommission } from "@/utils/write/commission";
import { createToast } from "mosha-vue-toastify";
import { useUserStore } from "@/store/user";
import "mosha-vue-toastify/dist/style.css";
import PaperModalContainer from "@/components/modal/PaperModalContainer.vue";

export default {
  name: "CommissionForm",
  components: {
    PaperModalContainer,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "create", // "create" 또는 "update"
      validator: (value) => ["create", "update"].includes(value),
    },
    commissionData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      userStore: useUserStore(),
      category: "",
      categoryOptions: [
        { label: "믹싱", value: "mixing" },
        { label: "일러스트", value: "illustration" },
        { label: "디자인", value: "design" },
        { label: "영상", value: "video" },
      ],
      name: "",
      link: "",
      description: "",
      thumbnailFile: null,
      uploading: false,
      // 기존 이미지 파일명 (수정 모드용)
      originalThumbnail: null,
      // 이미지 미리보기 URL
      thumbnailPreviewUrl: null,
    };
  },
  computed: {
    isUpdateMode() {
      return this.mode === "update";
    },
    modalTitle() {
      return this.isUpdateMode ? "~ 커미션 수정 ~" : "~ 커미션 등록 ~";
    },
    submitButtonText() {
      return this.isUpdateMode ? "수정" : "등록";
    },
    loadingText() {
      return "";
    },
  },
  watch: {
    commissionData: {
      immediate: true,
      handler(newData) {
        if (newData && this.isUpdateMode) {
          this.populateForm(newData);
        }
      },
    },
  },
  methods: {
    async populateForm(commissionData) {
      this.category = commissionData.category || "";
      this.name = commissionData.name || "";
      this.link = commissionData.link || "";
      this.description = commissionData.description || "";

      // 기존 썸네일 파일명 저장
      this.originalThumbnail = commissionData.thumbnail || null;

      // 기존 이미지가 있으면 미리보기 URL 가져오기
      if (this.originalThumbnail) {
        this.thumbnailPreviewUrl = await this.getImagePreviewUrl("commission", this.originalThumbnail);
      }
    },
    handleThumbnailUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.thumbnailFile = file;
        // 새 파일 선택 시 미리보기 업데이트
        const reader = new FileReader();
        reader.onload = (e) => {
          this.thumbnailPreviewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    async getImagePreviewUrl(path, fileName) {
      if (!fileName) return null;

      // 이미 HTTP URL인 경우 그대로 반환
      if (fileName.startsWith("http")) {
        return fileName;
      }

      // Storage에서 다운로드 URL 가져오기
      try {
        const imageRef = ref(storage, `${path}/${fileName}`);
        const url = await getDownloadURL(imageRef);
        return url;
      } catch (error) {
        console.error(`${path} 이미지 로드 실패 (경로: ${path}/${fileName}):`, error);
        return null;
      }
    },
    async handleSubmit() {
      if (this.isUpdateMode) {
        await this.update();
      } else {
        await this.create();
      }
    },
    async create() {
      // 유효성 검사
      if (this.name.trim() === "") {
        this.toast("커미션명을 입력해주세요", "default");
        return;
      }
      if (!this.category) {
        this.toast("카테고리를 선택해주세요", "default");
        return;
      }
      if (!this.thumbnailFile) {
        this.toast("썸네일을 선택해주세요", "default");
        return;
      }
      if (this.link.trim() === "") {
        this.toast("연결링크를 입력해주세요", "default");
        return;
      }
      if (!this.checkUrl(this.link)) {
        this.toast("올바른 URL 형식을 입력해주세요", "default");
        return;
      }

      const commissionData = {
        category: this.category,
        name: this.name.trim(),
        link: this.link.trim(),
        description: this.description?.trim() || "",
        thumbnail: "", // 파일명은 commission.js에서 생성됨
        createdBy: this.userStore.email,
        createdByNickname: this.userStore.nickname,
        createdAt: new Date(),
      };

      try {
        this.uploading = true;

        await createCommission(commissionData, this.thumbnailFile);

        this.cancelPopup();
        this.toast("등록 완료!", "default");
        this.renew();
      } catch (error) {
        this.toast("등록 중 에러가 발생했습니다", "danger");
        this.renew();
        console.error("Error creating commission:", error);
      } finally {
        this.uploading = false;
      }
    },
    async update() {
      // 유효성 검사
      if (this.name.trim() === "") {
        this.toast("커미션명을 입력해주세요", "default");
        return;
      }
      if (this.link.trim() === "") {
        this.toast("연결링크를 입력해주세요", "default");
        return;
      }
      if (!this.checkUrl(this.link)) {
        this.toast("올바른 URL 형식을 입력해주세요", "default");
        return;
      }

      const updateData = {
        category: this.category,
        name: this.name.trim(),
        link: this.link.trim(),
        description: this.description?.trim() || "",
        // 썸네일 파일명 (새 파일이 없으면 기존 값 유지)
        thumbnail: this.originalThumbnail || "",
        lastModifiedBy: this.userStore.email,
        lastModifiedByNickname: this.userStore.nickname,
        lastModifiedAt: new Date(),
      };

      try {
        this.uploading = true;

        await updateCommission(
          this.commissionData.id,
          updateData,
          this.thumbnailFile
        );

        this.cancelPopup();
        this.toast("수정이 완료되었습니다!", "default");
        this.renew();
      } catch (error) {
        this.toast("수정 중 에러가 발생했습니다", "danger");
        this.renew();
        console.error("Error updating commission:", error);
      } finally {
        this.uploading = false;
      }
    },
    cancelPopup() {
      this.$emit("close-popup");
    },
    renew() {
      this.category = "";
      this.name = "";
      this.link = "";
      this.description = "";
      this.thumbnailFile = null;
      this.originalThumbnail = null;
      this.thumbnailPreviewUrl = null;
    },
    checkUrl(url) {
      const urlPattern = /^https?:\/\/[^\s$.?#].[^\s]*$/i;
      return urlPattern.test(url);
    },
    toast(message, type) {
      createToast(message, {
        type: type,
        timeout: 3000,
        position: "top-center",
      });
    },
  },
};
</script>

<style module>
h5 {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  margin: 5px;
}

input {
  width: 100%;
  font-size: 14px;
}

.textInput {
  border: 1px solid #dddddd;
  border-radius: 5px;
  padding: 0px 5px;
}

.textInput:focus {
  border: 1px solid #aaaeb7;
  outline: none;
  box-shadow: -2px 0 0 0 rgba(102, 189, 255, 0.2),
    0 2px 0 0 rgba(102, 189, 255, 0.2), 0 -2px 0 0 rgba(102, 189, 255, 0.2),
    2px 0 0 0 rgba(102, 189, 255, 0.2);
}

.fileInput {
  border: none;
}

.textareaInput {
  width: 100%;
  border: 1px solid #dddddd;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  resize: vertical;
  min-height: 90px;
  box-sizing: border-box;
}

.textareaInput:focus {
  border-color: #66bdff;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.15);
}

.input {
  padding: 5px;
  margin-top: 10px;
}

.buttons {
  margin-top: 20px;
}

.cancel {
  width: 30%;
  padding: 15px;
  margin: 5px;
  border-radius: 5px;
  background-color: #cbcbcb;
  min-width: 60px;
}

.register {
  width: 30%;
  padding: 15px;
  margin: 5px;
  border-radius: 5px;
  background-color: rgb(102, 189, 255);
  min-width: 60px;
}

.spinner {
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 2px solid transparent;
  border-top: 2px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  box-sizing: border-box;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* 이미지 미리보기 스타일 */
.imagePreviewContainer {
  margin-bottom: 10px;
}

.imagePreview {
  position: relative;
  display: inline-block;
  max-width: 300px;
  max-height: 400px;
  border: 1px solid #ddd;
  border-radius: 5px;
  overflow: hidden;
  background-color: #f5f5f5;
}

.previewImage {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}

.newFileInfo {
  margin-top: 5px;
  font-size: 12px;
  color: #666;
  font-style: italic;
}

/* 예쁜 드롭다운 */
.selectWrapper {
  position: relative;
  width: 100%;
}

.selectInput {
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1px solid #dddddd;
  border-radius: 8px;
  padding: 6px 36px 6px 12px;
  font-size: 14px;
  height: 34px;
  background-color: #fff;
  transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;
  cursor: pointer;
}

.selectInput:focus {
  border-color: #66bdff;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.15);
  background-color: #ffffff;
}

.selectInput:disabled {
  background-color: #f7f7f7;
  cursor: not-allowed;
}

.selectArrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #666;
  pointer-events: none;
}

.selectHint {
  margin-top: 6px;
  font-size: 12px;
  color: #8aa3b2;
}
</style>
